import type { TableRowData, TableSortMode } from 'tableTypes'

/**
 * Performs sorting on a column that contains strings as values based on sort mode.
 *
 * @param {TableRowData} rowData - Row data of the table
 * @param {TableSortMode} sortMode - Ascending, descending or normal sort mode (which equals to the original array)
 * @param {number} index - Index of column where sorting must be performed
 * @returns Sorted row data array
 */
export const sortTextColumn = (
    rowData: TableRowData[],
    sortMode: TableSortMode,
    index: number
): TableRowData[] => {
    // Creating a new instance from original array
    const sortableArray = rowData.concat()

    if (index < 0) {
        return sortableArray
    } else {
        return sortableArray.sort((a, b) => {
            if (index >= a.cells.length || index >= b.cells.length) {
                return 0
            }

            const textA = a.cells[index].text
            const textB = b.cells[index].text

            if (typeof textA === 'string' && typeof textB === 'string') {
                switch (sortMode) {
                    case 'ASC':
                        return textA.localeCompare(textB)
                    case 'DESC':
                        return textB.localeCompare(textA)
                    default:
                        return 0
                }
            }

            return 0
        })
    }
}

/**
 * Performs sorting on a column that contains numbers as values based on sort mode.
 *
 * @param {TableRowData} rowData - Row data of the table
 * @param {TableSortMode} sortMode - Ascending, descending or normal sort mode (which equals to the original array)
 * @param {number} index - Index of column where sorting must be performed
 * @returns Sorted row data array
 */
export const sortNumberColumn = (
    rowData: TableRowData[],
    sortMode: TableSortMode,
    index: number
): TableRowData[] => {
    // Creating a new instance from original array
    const sortableArray = rowData.concat()

    if (index < 0) {
        return sortableArray
    } else {
        return sortableArray.sort((a, b) => {
            if (index >= a.cells.length || index >= b.cells.length) {
                return 0
            }

            const numberA = a.cells[index].text
            const numberB = b.cells[index].text

            if (typeof numberA === 'number' && typeof numberB === 'number') {
                switch (sortMode) {
                    case 'ASC':
                        return numberA - numberB
                    case 'DESC':
                        return numberB - numberA
                    default:
                        return 0
                }
            }

            return 0
        })
    }
}
